/**
 * ServiceOrderStatusGantt - Linha do tempo (Gantt) das situações da O.S. (BR-OS-055).
 *
 * Eixo de tempo real (abertura → agora/conclusão), barra por situação na cor do grupo,
 * marcadores (agendado, deslocamento, check-in), etapa atual pontilhada, selo "Mais lenta",
 * resumo por grupo com barra empilhada e legenda. Somente CSS e tokens do tema.
 *
 * RELEASE: 2026-10-18 v1.3.0 - Resumo usa cycleTotals: Sem agendamento inclui a logística (subtotal entre parênteses).
 * RELEASE: 2026-10-18 v1.2.0 - Grupo Sem agendamento.
 * RELEASE: 2026-10-18 v1.1.0 - Grupo Deslocamento (cor própria e rótulo).
 * RELEASE: 2026-10-18 v1.0.0 - Versão inicial.
 */
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { SERVICE_ORDER_STATUS_LABEL, type ServiceOrderStatus } from '@/hooks/useServiceOrders';
import {
  STATUS_GROUP_LABEL as OS_GROUP_LABEL, formatInsightDuration, ganttBars, ganttPosition, ganttTicks, cycleTotals,
  type StatusGroup, type StatusSegment,
} from '@/lib/serviceOrders/insights';
import { cn } from '@/lib/utils';

// Cores por grupo (tokens semânticos)
const OS_GROUP_BAR: Record<StatusGroup, string> = {
  logistics: 'bg-info', unscheduled: 'bg-muted-foreground/30', scheduling: 'bg-muted-foreground/60', travel: 'bg-accent-foreground/70', execution: 'bg-primary', review: 'bg-warning', done: 'bg-success',
};
const GROUP_ORDER: string[] = ['unscheduled', 'logistics', 'scheduling', 'travel', 'execution', 'review', 'done'];

const fmt = (iso: string) => new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const osLabel = (s: string) => s === 'travel' ? 'Deslocamento' : SERVICE_ORDER_STATUS_LABEL[s as ServiceOrderStatus] ?? s;

interface Marker { label: string; at: string | null }

/** v1.4.0: configuração opcional de grupos (ex.: Remessa). Sem ela, comportamento da O.S. */
export interface GanttGroupConfig {
  order: string[];
  label: Record<string, string>;
  color: Record<string, string>;
  groupOf: (status: string) => string;
  statusLabel: (status: string) => string;
}

interface Props { segments: StatusSegment[]; start: string; end: string; markers: Marker[]; config?: GanttGroupConfig }

// RELEASE: 2026-10-18 v1.4.0 - Prop `config` para reuso no modal da Remessa.
export function ServiceOrderStatusGantt({ segments, start, end, markers, config }: Props) {
  const GROUP_BAR = (config?.color ?? OS_GROUP_BAR) as Record<string, string>;
  const STATUS_GROUP_LABEL = (config?.label ?? OS_GROUP_LABEL) as Record<string, string>;
  const label = config?.statusLabel ?? osLabel;
  const bars = ganttBars(segments, start, end).map(b => (config ? { ...b, group: config.groupOf(b.status) as StatusGroup } : b));
  const ticks = ganttTicks(start, end);
  let totals: Record<string, number> & { logisticsWithinUnscheduled?: number };
  if (config) {
    totals = {};
    for (const s of segments) { const g = config.groupOf(s.status); totals[g] = (totals[g] ?? 0) + s.seconds; }
  } else totals = cycleTotals(segments); // v1.3.0
  const cycle = segments.reduce((a, s) => a + s.seconds, 0);
  const slowest = segments.length ? segments.reduce((a, b) => (b.seconds > a.seconds ? b : a)) : null;
  const usedGroups = (config?.order ?? GROUP_ORDER).filter(g => (totals[g] ?? 0) > 0);
  const pins = markers.map(m => ({ ...m, left: ganttPosition(m.at, start, end) })).filter(m => m.left !== null);

  return (
    <TooltipProvider delayDuration={100}>
      <div className="space-y-4">
        {/* Resumo: ciclo total + distribuição por grupo */}
        <div className="space-y-2">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <p className="text-sm text-muted-foreground">Ciclo total <span className="ml-1 text-lg font-semibold text-foreground">{formatInsightDuration(cycle)}</span></p>
            {usedGroups.map(g => (
              <span key={g} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className={cn('h-2 w-2 rounded-full', GROUP_BAR[g])} />{STATUS_GROUP_LABEL[g]}
                <span className="font-medium text-foreground">{formatInsightDuration(totals[g])}</span>
                {/* v1.3.0: detalhe da espera pela remessa dentro do Sem agendamento */}
                {g === 'unscheduled' && totals.logisticsWithinUnscheduled > 0 && (
                  <span className="inline-flex items-center gap-1">(<span className={cn('h-1.5 w-1.5 rounded-full', GROUP_BAR.logistics)} />remessa {formatInsightDuration(totals.logisticsWithinUnscheduled)})</span>
                )}
              </span>
            ))}
          </div>
          <div className="flex h-1.5 overflow-hidden rounded-full bg-muted">
            {usedGroups.map(g => <div key={g} className={GROUP_BAR[g]} style={{ width: `${(totals[g] / Math.max(1, cycle)) * 100}%` }} />)}
          </div>
        </div>

        {/* Gantt com rolagem horizontal só no gráfico (celular) */}
        <div className="overflow-x-auto">
          <div className="min-w-[560px]">
            {/* Eixo */}
            <div className="grid grid-cols-[130px_1fr_92px] gap-3">
              <span />
              <div className="relative h-5 border-b border-border/60">
                {ticks.map((t, i) => (
                  <span key={i} className={cn('absolute top-0 whitespace-nowrap text-[10px] text-muted-foreground',
                    i === 0 ? '' : i === ticks.length - 1 ? '-translate-x-full' : '-translate-x-1/2')} style={{ left: `${t.left}%` }}>{fmt(t.at)}</span>
                ))}
              </div>
              <span />
            </div>

            {/* Linhas */}
            <div className="relative mt-1">
              {bars.map((b, i) => {
                const current = b.end === null;
                const isSlowest = slowest === segments[i] && segments.length > 1;
                return (
                  <div key={`${b.status}-${i}`} className="grid grid-cols-[130px_1fr_92px] items-center gap-3 py-1.5">
                    <span className="truncate text-xs text-muted-foreground" title={label(b.status)}>{label(b.status)}</span>
                    <div className="relative h-5 rounded bg-muted/40">
                      {/* Linhas-guia das marcas */}
                      {ticks.slice(1, -1).map((t, k) => <span key={k} className="absolute inset-y-0 w-px bg-border/50" style={{ left: `${t.left}%` }} />)}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className={cn('absolute inset-y-0.5 rounded-sm shadow-sm transition-opacity hover:opacity-90', GROUP_BAR[b.group],
                            current && 'bg-[length:8px_8px] [background-image:linear-gradient(45deg,hsl(var(--background)/.35)_25%,transparent_25%,transparent_50%,hsl(var(--background)/.35)_50%,hsl(var(--background)/.35)_75%,transparent_75%)]')}
                            style={{ left: `${b.left}%`, width: `${b.width}%` }} />
                        </TooltipTrigger>
                        <TooltipContent className="text-xs">
                          <p className="font-medium">{label(b.status)}</p>
                          <p>Início: {fmt(b.start)}</p>
                          <p>Fim: {b.end ? fmt(b.end) : 'em andamento'}</p>
                          <p>Duração: {formatInsightDuration(b.seconds)}</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <div className="flex flex-col items-end leading-tight">
                      <span className="whitespace-nowrap text-xs font-semibold text-foreground">{formatInsightDuration(b.seconds)}</span>
                      {current ? <span className="text-[10px] font-medium text-primary">atual</span>
                        : isSlowest ? <span className="text-[10px] font-medium text-warning">Mais lenta</span> : null}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Marcadores */}
            {pins.length > 0 && (
              <div className="grid grid-cols-[130px_1fr_92px] gap-3 pt-1">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Marcos</span>
                <div className="relative h-8 border-t border-border/60">
                  {pins.map((p, i) => (
                    <Tooltip key={i}>
                      <TooltipTrigger asChild>
                        <div className="absolute top-0 flex -translate-x-1/2 flex-col items-center" style={{ left: `${p.left}%`, marginTop: i % 2 ? 14 : 0 }}>
                          <span className="-mt-1 h-2 w-2 rotate-45 bg-foreground" />
                          <span className="mt-0.5 whitespace-nowrap text-[10px] text-muted-foreground">{p.label}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="text-xs">{p.label}: {fmt(p.at as string)}</TooltipContent>
                    </Tooltip>
                  ))}
                </div>
                <span />
              </div>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
